import { LineStyle } from 'lightweight-charts'
import { CanvasSettings } from '../components/chartV2/chart.typings'
import tinycolor from 'tinycolor2'
import { CrosshairMode, LineWidth } from 'lightweight-charts'

/** 4th option in line style picker: solid, dotted, dashed, large_dashed, sparse_dotted */
export const DEFAULT_CROSSHAIR_LINE_STYLE = 'large_dashed' as const

export const defaultCanvasSettings: Required<
    Pick<
        CanvasSettings,
        | 'gridVisibility'
        | 'gridHorzColor'
        | 'gridHorzColorOpacity'
        | 'gridVertColor'
        | 'gridVertColorOpacity'
        | 'crosshairColor'
        | 'crosshairColorOpacity'
        | 'crosshairThickness'
        | 'crosshairLineStyle'
        | 'scaleTextColor'
        | 'scaleTextColorOpacity'
        | 'scaleTextFontSize'
        | 'scaleLinesColor'
        | 'scaleLinesColorOpacity'
    >
> = {
    gridVisibility: 'both',
    gridHorzColor: '#888888',
    gridHorzColorOpacity: 0.08,
    gridVertColor: '#888888',
    gridVertColorOpacity: 0.08,
    crosshairColor: '#888888',
    crosshairColorOpacity: 0.8,
    crosshairThickness: 1,
    crosshairLineStyle: DEFAULT_CROSSHAIR_LINE_STYLE,
    scaleTextColor: '#888888',
    scaleTextColorOpacity: 1,
    scaleTextFontSize: 12,
    scaleLinesColor: '#888888',
    scaleLinesColorOpacity: 0.06,
}

export const canvasLineStyleToLineStyle = (style: CanvasSettings['crosshairLineStyle']): LineStyle => {
    switch (style) {
        case 'solid':
            return LineStyle.Solid
        case 'dotted':
            return LineStyle.Dotted
        case 'dashed':
            return LineStyle.Dashed
        case 'large_dashed':
            return LineStyle.LargeDashed
        case 'sparse_dotted':
            return LineStyle.SparseDotted
        default:
            return LineStyle.LargeDashed
    }
}

export const mergeCanvasSettings = (settings?: CanvasSettings): typeof defaultCanvasSettings =>
    ({ ...defaultCanvasSettings, ...settings })

type CanvasLayoutTheme = {
    textSecondary: string
    divider: string
}

type BuildCanvasLayoutPartialOptions = {
    alignLabels?: boolean
    crosshairMode?: CrosshairMode
    includeLeftPriceScale?: boolean
    scaleMargins?: { top: number; bottom: number }
}

export const buildCanvasLayoutPartial = (
    settings: CanvasSettings | undefined,
    theme: CanvasLayoutTheme,
    options: BuildCanvasLayoutPartialOptions = {}
) => {
    const canvasSettings = mergeCanvasSettings(settings)
    const crosshairLineStyle = canvasLineStyleToLineStyle(canvasSettings.crosshairLineStyle)
    const crosshairColor = tinycolor(canvasSettings.crosshairColor)
        .setAlpha(canvasSettings.crosshairColorOpacity)
        .toRgbString()
    const crosshairWidth = canvasSettings.crosshairThickness as LineWidth
    const scaleTextColor = tinycolor(canvasSettings.scaleTextColor)
        .setAlpha(canvasSettings.scaleTextColorOpacity ?? 1)
        .toRgbString()
    const scaleBorderColor = canvasSettings.scaleLinesColor
        ? tinycolor(canvasSettings.scaleLinesColor)
            .setAlpha(canvasSettings.scaleLinesColorOpacity)
            .toRgbString()
        : theme.divider
    const scaleBorderVisible = !!canvasSettings.scaleLinesColor
    const alignLabels = options.alignLabels ?? true
    const crosshairMode = options.crosshairMode ?? CrosshairMode.Normal
    const scaleMargins = options.scaleMargins

    const horzGridVisible =
        canvasSettings.gridVisibility === 'both' || canvasSettings.gridVisibility === 'horizontal'
    const vertGridVisible =
        canvasSettings.gridVisibility === 'both' || canvasSettings.gridVisibility === 'vertical'
    const horzGridColor = tinycolor(canvasSettings.gridHorzColor)
        .setAlpha(canvasSettings.gridHorzColorOpacity ?? 0.08)
        .toRgbString()
    const vertGridColor = tinycolor(canvasSettings.gridVertColor)
        .setAlpha(canvasSettings.gridVertColorOpacity ?? 0.08)
        .toRgbString()

    const priceScaleBase = {
        alignLabels,
        borderColor: scaleBorderColor,
        borderVisible: scaleBorderVisible,
        textColor: scaleTextColor,
        ...(scaleMargins ? { scaleMargins } : {}),
    }

    return {
        layout: {
            textColor: scaleTextColor,
            fontSize: canvasSettings.scaleTextFontSize,
        },
        grid: {
            horzLines: {
                visible: horzGridVisible,
                color: horzGridColor,
            },
            vertLines: {
                visible: vertGridVisible,
                color: vertGridColor,
            },
        },
        timeScale: {
            borderColor: scaleBorderColor,
            borderVisible: scaleBorderVisible,
            allowBoldLabels: false,
        },
        rightPriceScale: priceScaleBase,
        ...(options.includeLeftPriceScale !== false
            ? { leftPriceScale: priceScaleBase }
            : {}),
        crosshair: {
            mode: crosshairMode,
            horzLine: {
                color: crosshairColor,
                width: crosshairWidth,
                style: crosshairLineStyle,
            },
            vertLine: {
                color: crosshairColor,
                width: crosshairWidth,
                style: crosshairLineStyle,
            },
        },
    }
}
